import React from 'react';

import { Box, Button, Grid, Stack, Typography } from '@mui/material';

import { Nullable } from '@clearverify/types';
import { formatReportUpdatedDate, parseNullableValue } from '@clearverify/utils';

import { EmploymentDataInfoFragment } from 'src/api';
import { FullscreenLoader, Watermark } from 'src/components/common';
import { EmploymentDataDetails } from 'src/components/employmentData/EmploymentDataDetails';
import { useScreenInfo } from 'src/hooks/common';

interface PositionDetailsContentProps {
  employmentData: Nullable<EmploymentDataInfoFragment>;
  onBack?: () => void;
  onDataDispute?: (companyId: string) => void;
  isWatermarked?: boolean;
  loading?: boolean;
  hideDataDispute?: boolean;
}

export const PositionDetailsContent: React.FC<PositionDetailsContentProps> = ({
  employmentData,
  onBack,
  onDataDispute,
  isWatermarked,
  loading,
  hideDataDispute,
}) => {
  const { isMobile } = useScreenInfo();

  const reportUpdateDate = formatReportUpdatedDate(employmentData?.meta.updatedAt ?? new Date());

  const handleDataDisputeClick = React.useCallback(() => {
    if (employmentData?.meta.companyId) {
      onDataDispute?.(employmentData.meta.companyId);
    }
  }, [employmentData, onDataDispute]);

  if (loading) {
    return <FullscreenLoader />;
  }

  if (!employmentData) {
    return (
      <Box textAlign="center">
        <Typography variant="body2">
          Employment Data not found. Please contact support for assistance.
        </Typography>
        <Box mt={2}>
          <Button variant="outlined" color="inherit" onClick={onBack}>
            Back
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <Stack direction={isMobile ? 'column' : 'row'} gap={2}>
            <Box mr="auto">
              <Typography variant="h5" mb={1}>
                Employee Data from{' '}
                {parseNullableValue(employmentData.companyInformation.registeredBusinessName)}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Current as of: {reportUpdateDate}
              </Typography>
            </Box>
            {onBack && (
              <Box>
                <Button variant="outlined" color="inherit" onClick={onBack}>
                  Back
                </Button>
              </Box>
            )}

            {!isMobile && !hideDataDispute && (
              <Box>
                <Button onClick={handleDataDisputeClick} color="error" variant="contained">
                  Dispute Data
                </Button>
              </Box>
            )}
          </Stack>
        </Grid>
        <Grid size={12}>
          <Watermark text="Not for verification purposes" hidden={!isWatermarked}>
            <EmploymentDataDetails employmentData={employmentData} />
          </Watermark>
        </Grid>
      </Grid>
      {isMobile && !hideDataDispute && (
        <Box display="flex" justifyContent="center">
          <Button onClick={handleDataDisputeClick} color="error" variant="outlined">
            Dispute Data
          </Button>
        </Box>
      )}
    </React.Fragment>
  );
};
